import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserIcon, ShieldIcon } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";

export default function AuthStatus() {
  const { user, isAuthenticated, isLoading, signOut } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  
  useEffect(() => {
    const checkAdminStatusAndUserType = async () => {
      if (!user) return;
      try {
        const { data: profileData, error } = await supabase
          .from('accounts')
          .select('full_name, user_type, is_admin')
          .eq('id', user.id)
          .single();
        if (!error && profileData) {
          setIsAdmin(Boolean(profileData.is_admin));
          setUserType(profileData.user_type || null);
        }
      } catch (error) {
        console.error('Error checking admin status/user_type:', error);
      }
    };
    checkAdminStatusAndUserType();
  }, [user]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await signOut();
    setIsSigningOut(false);
  };

  if (isLoading) {
    return (
      <Button variant="ghost" size="icon" disabled>
        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></span>
        <span className="sr-only">Loading</span>
      </Button>
    );
  }

  if (isAuthenticated && user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <UserIcon className="h-5 w-5" />
            <span className="sr-only">User menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>
            {user.phone ? user.phone : "My Account"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isAdmin && (
            <DropdownMenuItem asChild>
              <Link to="/admin" className="flex items-center gap-2">
                <ShieldIcon className="h-4 w-4" />
                Admin Dashboard
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link to="/buyer-dashboard">Buyer Dashboard</Link>
          </DropdownMenuItem>
          {userType !== 'buyer' && (
            <DropdownMenuItem asChild>
              <Link to="/supplier-dashboard">Supplier Dashboard</Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link to="/account">Account</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/orders">Orders</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/short-list">Short List</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={handleSignOut}
            disabled={isSigningOut}
          >
            {isSigningOut ? "Signing out..." : "Sign out"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button variant="outline" className="ml-1" asChild>
      <Link to="/login" className="flex items-center gap-2">
        <UserIcon className="h-4 w-4" />
        <span>Login/Register</span>
      </Link>
    </Button>
  );
}
